import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/layout/AppShell";
import ClientsList from "@/pages/ClientsList";
import ClientDetail from "@/pages/ClientDetail";
import Integrations from "@/pages/Integrations";
import Alerts from "@/pages/Alerts";
import Login from "@/pages/Login";
import UsersPage from "@/pages/Users";
import AiAssistantPage from "@/pages/AiAssistantPage";
import AiAnalyticsPage from "@/pages/AiAnalyticsPage";
import AiPaidPage from "@/pages/AiPaidPage";
import PublicReport from "@/pages/PublicReport";
import ReportsConfig from "@/pages/ReportsConfig";
import InsightsPage from "@/pages/InsightsPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (role && user.role !== role) return <Navigate to="/clients" replace />;
  return children;
}

function ClientLanding() {
  // Clients are redirected to their first assigned client; narwhal lands on /clients.
  const { user } = useAuth();
  if (user?.role === "client" && user.assigned_client_ids?.[0]) {
    return <Navigate to={`/clients/${user.assigned_client_ids[0]}`} replace />;
  }
  return <Navigate to="/clients" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/r/:token" element={<PublicReport />} />
            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route path="/" element={<ClientLanding />} />
              <Route path="/dashboard" element={<Navigate to="/clients" replace />} />
              <Route path="/clients" element={<RequireAuth role="narwhal"><ClientsList /></RequireAuth>} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/integrations" element={<RequireAuth role="narwhal"><Integrations /></RequireAuth>} />
              <Route path="/alerts" element={<RequireAuth role="narwhal"><Alerts /></RequireAuth>} />
              <Route path="/users" element={<RequireAuth role="narwhal"><UsersPage /></RequireAuth>} />
              <Route path="/ai-assistant" element={<RequireAuth role="narwhal"><AiAssistantPage /></RequireAuth>} />
              <Route path="/ai-analytics" element={<RequireAuth role="narwhal"><AiAnalyticsPage /></RequireAuth>} />
              <Route path="/paid-analyst" element={<RequireAuth role="narwhal"><AiPaidPage /></RequireAuth>} />
              <Route path="/reports-config" element={<RequireAuth role="narwhal"><ReportsConfig /></RequireAuth>} />
              <Route path="/insights" element={<RequireAuth role="narwhal"><InsightsPage /></RequireAuth>} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
